import { useEnv } from '@sparklayer-io/spark-dash-core/context/env-context';
import {
  useHandlers,
  withPageProviders,
} from '@sparklayer-io/spark-dash-pages/context/page-context';
import { cn } from '@sparklayer-io/spark-dash-ui/lib/styles';
import { useQuery } from '@tanstack/react-query';
import { Alert, Card, Heading, Page, Pressable, Skeleton, Text } from '@ui/themed';
import { t } from 'i18next';
import type { I18nPrefix } from '../../../i18n/prefix';
import { useStaffApi } from '../hooks/use-staff-api';
import type { ActionSummary, StaffActionCategory } from '../types';

const PAGE = 'features:staff.pages.manage_actions_page' satisfies I18nPrefix;

type ManageActionsPageConfig = {
  category: StaffActionCategory;
  title: string;
  subtitle: (teamName: string) => string;
};

type ActionListProps = {
  actions: ActionSummary[];
  category: StaffActionCategory;
  disabled?: boolean;
};

function ActionList({ actions, category, disabled = false }: ActionListProps) {
  const { navigationHandler } = useHandlers();

  return (
    // Inset the focus ring so the list's overflow-hidden doesn't clip it
    <div className="divide-y divide-spark-border-subtle overflow-hidden rounded-lg border border-spark-border-subtle [&_button]:focus-visible:ring-inset">
      {actions.map((action) => (
        <div key={action.type}>
          <Pressable
            onClick={() =>
              navigationHandler.navigate(`/staff/${category}/${action.type}`, {
                excludeSiteEnv: true,
              })
            }
            disabled={disabled}
            fullWidth
            type="flush"
          >
            <div className={cn('w-full p-4 text-start', disabled && 'bg-spark-surface-muted')}>
              <Heading level="h3" style="sm" weight="semibold">
                {action.name}
                {disabled && (
                  <>
                    {' '}
                    —{' '}
                    <span className="text-sm text-spark-warning-soft-text italic">
                      {action.reason}
                    </span>
                  </>
                )}
              </Heading>
              <Text variant="secondary" size="sm">
                {action.description}
              </Text>
            </div>
          </Pressable>
        </div>
      ))}
    </div>
  );
}

function ManageActionsPageInner({ category, title, subtitle }: ManageActionsPageConfig) {
  const env = useEnv();
  const { staffApi } = useStaffApi();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['staff-actions', env.team.id, category],
    queryFn: () => staffApi.actions(env.team.id, category),
  });

  if (isLoading) return <Skeleton />;

  if (isError || !data) {
    return (
      <Alert variant="error" headingLevel="h3">
        {t(`${PAGE}.load_error`)}
      </Alert>
    );
  }

  const { allowed: allowedActions, unavailable: unavailableActions } = data.actions;

  return (
    <Page
      title={title}
      description={subtitle(env.team.name)}
      learnMoreUrl="https://docs.sparklayer.io"
      footerDescription={t(`${PAGE}.footer`)}
    >
      <Card>
        <Card.Body>
          {allowedActions.length === 0 ? (
            <Alert variant="warning" headingLevel="h3">
              {t(`${PAGE}.no_actions`)}
            </Alert>
          ) : (
            <ActionList actions={allowedActions} category={category} />
          )}
        </Card.Body>
      </Card>
      {unavailableActions.length > 0 && (
        <Card>
          <Card.Header headingLevel="h2">{t(`${PAGE}.unavailable_heading`)}</Card.Header>
          <Card.Body>
            <Text variant="secondary">{t(`${PAGE}.unavailable_description`)}</Text>
            <ActionList actions={unavailableActions} category={category} disabled />
          </Card.Body>
        </Card>
      )}
    </Page>
  );
}

export const ManageBillingPage = withPageProviders(() => (
  <ManageActionsPageInner
    category="manage-billing"
    title={t(`${PAGE}.manage_billing.title`)}
    subtitle={(name) => t(`${PAGE}.manage_billing.description`, { name })}
  />
));

export const ManageTeamPage = withPageProviders(() => (
  <ManageActionsPageInner
    category="manage-team"
    title={t(`${PAGE}.manage_team.title`)}
    subtitle={(name) => t(`${PAGE}.manage_team.description`, { name })}
  />
));
